/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from 'react'
import { InputField, Modal, MultiselectCombobox } from '@enamelai/design-system'
import { getAllProcedures } from '@services/api/procedure.api'
import { logger } from '@utils/safeLogger'

type AddEditBundledProps = {
  show: boolean
  onHide: () => void
  actions: any
  recordToEdit?: any | null
  /** Kept for callers that open this from another modal; DS modals stack by DOM order. */
  nestedInModal?: boolean
}

type FormData = {
  bundled_name: string
  bundled_code: string
  procedures: any[]
}

const emptyFormData = (): FormData => ({
  bundled_name: '',
  bundled_code: '',
  procedures: [],
})

const AddEditBundled: React.FC<AddEditBundledProps> = ({
  show,
  onHide,
  actions,
  recordToEdit = null,
}: AddEditBundledProps): React.ReactElement => {
  const [formData, setFormData] = useState<FormData>(emptyFormData())
  const [validationErrors, setValidationErrors] = useState<any>({})
  const [procedures, setProcedures] = useState<any[]>([])
  const idToEdit: string | null = recordToEdit?.bundled_procedure_id ?? null

  useEffect(() => {
    if (recordToEdit) {
      setFormData({
        bundled_name: recordToEdit.bundled_name,
        bundled_code: recordToEdit.bundled_code,
        procedures: recordToEdit.bundled_procedure_items.flatMap(
          (item: any) => item.procedure,
        ),
      })
    }
  }, [recordToEdit])

  useEffect(() => {
    getAllProcedures(true, true)
      .then((res) => {
        if ('procedures' in res.data) setProcedures(res.data.procedures || [])
      })
      .catch((error) => logger.error('Error fetching procedures:', error))
  }, [])

  const procedureById = useMemo(() => {
    const map = new Map<string, any>()
    formData.procedures.forEach((p) => map.set(p.procedure_id, p))
    procedures.forEach((p) => map.set(p.procedure_id, p))
    return map
  }, [procedures, formData.procedures])

  const procedureOptions = useMemo(
    () =>
      Array.from(procedureById.values())
        .sort((a, b) => a.procedure_code.localeCompare(b.procedure_code))
        .map((p) => ({
          value: p.procedure_id,
          label: `${p.procedure_code} - ${p.description}`,
          chipLabel: p.procedure_code,
        })),
    [procedureById],
  )

  const resetValues = (): void => {
    setFormData(emptyFormData())
    setValidationErrors({})
  }

  const handleCancelClick = (): void => {
    resetValues()
    onHide()
  }

  const handleChange = (name: string, value: string | number): void => {
    setFormData({
      ...formData,
      [name]: value,
    })
    if (validationErrors[name]) {
      delete validationErrors[name]
    }
  }

  const handleProceduresChange = (ids: string[]): void => {
    setFormData((prevState: FormData) => ({
      ...prevState,
      procedures: ids
        .map((id) => procedureById.get(id))
        .filter((p): p is any => !!p),
    }))
    if (validationErrors.procedures) {
      delete validationErrors.procedures
    }
  }

  const validateForm = (): boolean => {
    const errors: any = {}
    if (!formData.bundled_name) {
      errors.bundled_name = 'Bundle name is required'
    }
    if (!formData.bundled_code) {
      errors.bundled_code = 'Bundle code is required'
    }
    if (formData.procedures.length === 0) {
      errors.procedures = 'At least one procedure is required'
    }
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSaveClick = async (): Promise<void> => {
    if (!validateForm()) {
      return
    }
    await actions.save(idToEdit, formData)
  }

  return (
    <Modal
      open={show}
      onClose={handleCancelClick}
      size="sm"
      title={idToEdit ? 'Edit bundled procedure' : 'Add bundled procedure'}
      closeOnBackdrop={false}
      className="bf-modal"
      secondaryLabel="Cancel"
      onSecondaryClick={handleCancelClick}
      primaryLabel="Save"
      onPrimaryClick={() => {
        void handleSaveClick()
      }}
    >
      <div className="bf-modal__form">
        <div className="bf-modal__row">
          <InputField
            variant="text"
            label="Bundle name"
            placeholder="Enter bundle name"
            value={formData.bundled_name}
            onChange={(e) => handleChange('bundled_name', e.target.value)}
            error={!!validationErrors.bundled_name}
            helperText={validationErrors.bundled_name}
            fullWidth
          />
          <InputField
            variant="text"
            label="Bundle code"
            placeholder="e.g.  B1234"
            value={formData.bundled_code}
            onChange={(e) => handleChange('bundled_code', e.target.value)}
            error={!!validationErrors.bundled_code}
            helperText={validationErrors.bundled_code}
            fullWidth
          />
        </div>
        <MultiselectCombobox
          label="Procedure codes"
          placeholder="Search procedures to add"
          options={procedureOptions}
          value={formData.procedures.map((p) => p.procedure_id)}
          onChange={handleProceduresChange}
          error={!!validationErrors.procedures}
          helperText={validationErrors.procedures}
        />
      </div>
    </Modal>
  )
}

export default AddEditBundled
